import { ChangeEvent, useEffect, useState } from 'react';
import axiosInstance from '../../axios/axiosInstance';
import { BibleVersesApi } from '../bible-verse/bible-verse-api';
import { BibleVerse } from '../bible-verse/types';
import { AnniversaryTemplatePreview, getBodyParameters, getHeaderParameters } from './AnniversaryTemplatePreview';

import { AnniversaryMessageRequest, AnniversaryOccasion, getOccasionDisplayName, User, WhatsAppTemplate, Zone } from './types';
import { useNavigate } from 'react-router-dom';
import { calculateYearsSince } from './util';
import { useTheme } from '../../context/ThemeContext';
import DatePicker from '../../components/date-picker/DatePicker';
import { BibleApi } from '../bible-management/bible-api';

const bibleVerseApi = new BibleVersesApi()
const bibleApi = new BibleApi()


export default function AnniversaryListPage() {

  const { theme } = useTheme();
  const navigate = useNavigate()

  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [anniversaryOccasions, setAnniversaryOccasions] = useState<AnniversaryOccasion[]>([]);
  const [filteredOccasions, setFilteredOccasions] = useState<AnniversaryOccasion[]>([]);
  const [selectedOccasions, setSelectedOccasions] = useState<AnniversaryOccasion[]>([]);
  const [selectAll, setSelectAll] = useState(false)

  const [zones, setZones] = useState<Zone[]>([]);

  // Filter states
  const [filterType, setFilterType] = useState<string>('ALL');
  const [filterGender, setFilterGender] = useState<string>('ALL');
  const [filterZone, setFilterZone] = useState<string>('ALL');
  const [dateRangeStart, setDateRangeStart] = useState<string>('');
  const [dateRangeEnd, setDateRangeEnd] = useState<string>('');
  const [sendStatus, setSendStatus] = useState<{
    success: boolean;
    message: string;
    job_id?: number;
  } | null>(null);

  // Preview states
  const [showPreview, setShowPreview] = useState(false);
  const [previewOccasion, setPreviewOccasion] = useState<AnniversaryOccasion | null>(null);
  const [previewIndex, setPreviewIndex] = useState(0);

  // Summary states
  const [showConfirmSend, setShowConfirmSend] = useState(false);
  const [showResponse, setshowResponse] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [verses, setVerses] = useState<BibleVerse[]>([]);
  const [whatsappTemplates, setWhatsappTemplates] = useState<WhatsAppTemplate[]>([])

  // const fetchVerses = async () => {
  //   try {
  //     // setLoading(true);
  //     const verses = await bibleVerseApi.getAllVerses()
  //     console.log(verses)
  //     setVerses(verses);
  //   } catch (err) {
  //     // setError(err instanceof Error ? err.message : 'An error occurred');
  //   } finally {
  //     // setLoading(false);
  //   }
  // };


  const fetchVerses = async (occasion_id: string, language_code: string) => {
    try {
      // setLoading(true);
      const verses = await bibleApi.getAllOccasionVerseByOccassion(occasion_id, "en")

      const bVerses = verses.map(v => ({
        id: v.id,
        verse_text: v.verse_text,
        reference: `${v.book_name} ${v.chapter_index}:${v.verse_index}`,
        is_default: v.is_default,
        occasion_name: v.occasion_name,
        occasion_index: v.occasion_index
      } as BibleVerse))
      setVerses(bVerses);
    } catch (err) {
      // setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      // setLoading(false);
    }
  };


  const fetchUsers = async () => {
    // setLoading(true);
    try {
      const resp: any = await axiosInstance.get(
        `/api/users/anniversaries?date=${selectedDate}`
      );
      if (resp.status === 200) {
        const occasions = (resp.data.data || []).map((d: any) => ({
          ...d, id: `${d.type}:${d.user.id}`,
          selectedWhatsappTemplate: whatsappTemplates.find(w => w.name.match(RegExp(`anniversary_${d.type}_default`))),
          selectedVerse: verses.find(v => v.occasion_index?.match(RegExp(`${d.type}`)) && v.is_default === true)
        }))
        setAnniversaryOccasions(occasions);
      }
    } catch (error: any) {
      // toast.error("Error getting users");
    }
  };

  useEffect(() => {

    fetchUsers()

    fetchVerses("birthday", "en")

    setSelectedOccasions([]);

  }, [selectedDate, whatsappTemplates]);


  useEffect(() => {
    let filtered = [...anniversaryOccasions];

    // Filter by type
    if (filterType !== 'ALL') {
      filtered = filtered.filter(occasion => occasion.type === filterType);
    }

    // Filter by gender
    if (filterGender !== 'ALL') {
      filtered = filtered.filter(occasion => occasion.user.gender === filterGender);
    }

    // Filter by zone
    if (filterZone !== 'ALL') {
      filtered = filtered.filter(occasion => occasion.user.zone.zone_name === filterZone);
    }

    // Filter by date range
    if (dateRangeStart && dateRangeEnd) {
      filtered = filtered.filter(occasion => {
        const occasionDate = new Date(occasion.date);
        const startDate = new Date(dateRangeStart);
        const endDate = new Date(dateRangeEnd);
        return occasionDate >= startDate && occasionDate <= endDate;
      });
    }

    setFilteredOccasions(filtered);
  }, [anniversaryOccasions, filterType, filterGender, filterZone, dateRangeStart, dateRangeEnd]);

  const handleOccasionToggle = (occasion: AnniversaryOccasion) => {
    setSelectedOccasions(prev => {
      const isSelected = prev.some(o => o.id === occasion.id);
      if (isSelected) {
        return prev.filter(o => o.id !== occasion.id);
      } else {
        return [...prev, occasion];
      }
    });
  };

  const handlePreview = (occasion: AnniversaryOccasion) => {
    setPreviewOccasion(occasion);
    setPreviewIndex(0);
    setShowPreview(true);
  };

  const handleNextPreview = () => {
    if (previewOccasion) {
      const currentIndex = selectedOccasions.findIndex(o => o.id === previewOccasion.id);
      const nextIndex = (currentIndex + 1) % selectedOccasions.length;
      setPreviewOccasion(selectedOccasions[nextIndex]);
      setPreviewIndex(nextIndex);
    }
  };

  const handlePreviousPreview = () => {
    if (previewOccasion) {
      const currentIndex = selectedOccasions.findIndex(o => o.id === previewOccasion.id);
      const prevIndex = currentIndex === 0 ? selectedOccasions.length - 1 : currentIndex - 1;
      setPreviewOccasion(selectedOccasions[prevIndex]);
      setPreviewIndex(prevIndex);
    }
  };

  const handleSendMessages = async (e: any) => {

    setIsSending(true);
    handleConfirmSend(e).then(() => {
      setIsSending(false)
      setshowResponse(true)
    }).catch(e => {

      setIsSending(false)
      alert(e)
    })
  };

  function handleDetailsButton() {
    if (sendStatus?.job_id)
      navigate(`/message-transactions/${sendStatus?.job_id}`)

  }


  const apiFetchZones = async () => {
    try {
      const resp: any = await axiosInstance.get(`/api/zone/all`);
      if (resp.status === 200) {
        setZones(resp.data.data || []);
      }
    } catch (error: any) {
      // toast.error("Error getting users");
    }
  };


  const getOccasionColor = (type: string) => {
    switch (type) {
      case 'birthday':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'wedding':
        return 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200';
      case 'baptism':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'communion':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const getSummaryByType = () => {
    const summary: Record<string, number> = {};
    selectedOccasions.forEach(occasion => {
      const type = getOccasionDisplayName(occasion.type);
      summary[type] = (summary[type] || 0) + 1;
    });
    return summary;
  };

  const apiFetchWhatsappTemplates = () => {
    const token = localStorage.getItem("token")
    fetch("/api/messages/whatsapp-templates", { headers: { Authorization: `Bearer ${token}` } }).then(d => {
      d.json().then((d) => {
        setWhatsappTemplates(d.data || [])
      })
    })

  }

  useEffect(() => {
    apiFetchWhatsappTemplates()
    apiFetchZones();

  }, [])

  const handleConfirmSend = async (e: React.FormEvent) => {

    e.preventDefault();

    try {

      const messages = selectedOccasions.map(occassion => {

        let filteredTemplates = whatsappTemplates.filter((d: any) => d.name.match(RegExp(`anniversary_${occassion.type}`)))

        if (filteredTemplates.length == 0)
          filteredTemplates = whatsappTemplates.filter((d: any) => d.name.match(RegExp(`anniversary_generic`)))

        const messageRequest: AnniversaryMessageRequest = {
          whatsapp_message: {
            // template_name: filteredTemplates[0].name,
            template_name: occassion.selectedWhatsappTemplate?.name || filteredTemplates[0].name,
            header_parameters: getHeaderParameters(occassion, verses),
            body_parameters: getBodyParameters(occassion, verses)
          },
          recipient_member_id: occassion.user.id,
          occassion: occassion.type,
          occassion_date: new Date(selectedDate),
          channels: {
            sms: true,
            whatsapp: true
          },
          sms_message: {
            use_whatsapp_template: true,
            text: ''
          },
        };

        return messageRequest

      })


      const token = localStorage.getItem("token");

      const response = await fetch("/api/batch-jobs/send-anniversary-message", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(messages),
      });

      const result = await response.json();

      if (result.status == "ok") {
        setSendStatus({
          success: true,
          message: "Batch Started",
          job_id: result.data.job_id
        });
      } else {
        setSendStatus({
          success: false,
          message: result.message,
        });
      }
    } catch (error) {

      setSendStatus({
        success: false,
        message: "Failed to send message",
      });
    } finally {
      setIsSending(false);
    }
  };


  function handlePreviewSelected(event: any): void {
    setPreviewOccasion(selectedOccasions[0])
    setShowPreview(true)
  }

  function handleSelectAll(event: ChangeEvent<HTMLInputElement>): void {
    if (event.target.checked === true)
      setSelectedOccasions([...filteredOccasions])
    else
      setSelectedOccasions([])
  }



  function handleSelectVerse(occasion: AnniversaryOccasion, value: number): void {
    occasion.selectedVerse = verses.find(v => v.id === value);

    setFilteredOccasions([...filteredOccasions])

  }

  function handleSelectTemplate(occasion: AnniversaryOccasion, value: string): void {
    occasion.selectedWhatsappTemplate = whatsappTemplates.find(w => w.name === value);
    setSelectedOccasions([...selectedOccasions])
  }

  function handleChangeOccasionTemplate(template: WhatsAppTemplate): void {
    if (previewOccasion) {
      previewOccasion.selectedWhatsappTemplate = template
      setPreviewOccasion(previewOccasion)
    }
  }

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-neutral-900 text-white' : 'bg-gray-50 text-neutral-900'}`}>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Anniversaries</h1>
          <p className="text-neutral-600 dark:text-neutral-400">
            View and manage people with anniversaries on a specific date
          </p>
        </div>
        <div>
          {sendStatus?.message}
        </div>
        {/* Date Selection */}
        <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Select Date</h2>
          <div className="flex items-end space-x-4">
            <div>
              <label className="block text-sm font-medium text-black dark:text-white mb-1">
                Date
              </label>
              <DatePicker
                value={selectedDate}
                onChange={(date) => setSelectedDate(date)}
                placeholder="Select date"
                className="w-full"
              />
            </div>
            <button
              onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
            >
              Today
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-black dark:text-white mb-1">
                Anniversary Type
              </label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-neutral-700 dark:text-white"
              >
                <option value="ALL">All Types</option>
                <option value="birthday">Birthday</option>
                <option value="wedding">Wedding Anniversary</option>
                <option value="baptism">Baptism Anniversary</option>
                <option value="communion">Communion Anniversary</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-black dark:text-white mb-1">
                Gender
              </label>
              <select
                value={filterGender}
                onChange={(e) => setFilterGender(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-neutral-700 dark:text-white"
              >
                <option value="ALL">All Genders</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-black dark:text-white mb-1">
                Zone
              </label>
              <select
                value={filterZone}
                onChange={(e) => setFilterZone(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-neutral-700 dark:text-white"
              >
                <option value="ALL">All Zones</option>
                {zones.map(zone => (
                  <option key={zone.zone_name} value={zone.id}>{zone.zone_name}</option>
                ))}
              </select>
            </div>

            {/*<div>
              <label className="block text-sm font-medium text-black dark:text-white mb-1">
                Date Range
              </label>
              <div className="flex space-x-2">
                <DatePicker
                  value={dateRangeStart}
                  onChange={(date) => setDateRangeStart(date)}
                  placeholder="Start"
                  className="flex-1"
                />
                <DatePicker
                  value={dateRangeEnd}
                  onChange={(date) => setDateRangeEnd(date)}
                  placeholder="End"
                  className="flex-1"
                />
              </div>
            </div> */}
          </div>
        </div>

        {/* Results */}
        <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-semibold">
                Results ({filteredOccasions.length} found)
              </h2>
              {filteredOccasions.length > 0 ? <>  <input
                type="checkbox"
                // checked={ }
                onChange={handleSelectAll}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />  {selectAll ? 'Un Select All' : 'Select All'}</> : <></>}
            </div>

            {selectedOccasions.length > 0 && (
              <div className="flex space-x-2">

                <button
                  onClick={handlePreviewSelected}
                  className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
                >
                  Preview ({selectedOccasions.length})
                </button>
                <button
                  onClick={() => setShowConfirmSend(true)}
                  disabled={isSending}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors disabled:opacity-50"
                >
                  {isSending ? 'Sending...' : `Send Messages (${selectedOccasions.length})`}
                </button>
              </div>
            )}
          </div>

          {filteredOccasions.length === 0 ? (
            <div className="text-center py-8 text-neutral-500 dark:text-neutral-400">
              No anniversaries found for the selected criteria
            </div>
          ) : (
            <div className="space-y-4">
              {filteredOccasions.map((occasion) => (
                <div
                  key={occasion.id}
                  className={`border rounded-lg p-4 transition-colors ${selectedOccasions.some(o => o.id === occasion.id)
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-neutral-200 dark:border-neutral-700'
                    }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <input
                        type="checkbox"
                        checked={selectedOccasions.some(o => o.id === occasion.id)}
                        onChange={() => handleOccasionToggle(occasion)}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <img
                        src={occasion.user.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${occasion.user.name}`}
                        alt={occasion.user.name}
                        className="w-12 h-12 rounded-full"
                      />
                      <div>
                        <h3 className="font-semibold">{occasion.user.name}</h3>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400">
                          {/* {occasion.user.email} • */}
                          {occasion.user.phone}
                        </p>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400">
                          {/* {occasion.user.zone} • {occasion.user.department} • {occasion.user.designation} */}
                          {/* {"Department"} */}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getOccasionColor(occasion.type)}`}>
                        {getOccasionDisplayName(occasion.type)}
                      </span>
                      <span className="text-sm text-neutral-600 dark:text-neutral-400">
                        {calculateYearsSince(occasion.date)} years
                      </span>
                      <select className="w-48" onChange={(e) => handleSelectVerse(occasion, parseInt(e.target.value))} value={occasion.selectedVerse?.id || ''}> 
                        <option value="">Select Verse</option>
                        {verses
                          // .filter(v => v.occasion_index == occasion.type)
                          .map(v => {
                            return <option value={v.id} key={v.id}>{v.reference}</option>
                          })}
                      </select>
                      <select className="w-64" onChange={(e) => handleSelectTemplate(occasion, e.target.value)} value={occasion.selectedWhatsappTemplate?.name || ''}>
                        <option value="">Select Template</option>
                        {whatsappTemplates.filter(w => w.name.match(RegExp(`anniversary_${occasion.type}`))).map(w => <option value={w.name}>{w.name}</option>)}
                      </select>

                      <button
                        onClick={() => handlePreview(occasion)}
                        className="px-3 py-1 bg-gray-100 dark:bg-neutral-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-neutral-600 transition-colors text-sm"
                      >
                        Preview
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Preview Modal */}
        {showPreview && previewOccasion && (
          <AnniversaryTemplatePreview
            onClose={() => setShowPreview(false)}
            selectedOccasions={selectedOccasions}
            selectedIndex={previewIndex}
            occassion={previewOccasion}
            handlePreviousPreview={handlePreviousPreview}
            handleNextPreview={handleNextPreview}
            verses={verses}
            handleChangeTemplate={handleChangeOccasionTemplate}
            whatsappTemplates={whatsappTemplates}
          />
        )}

        {/* Summary Modal */}
        {showConfirmSend && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg w-full max-w-md">
              <div className="p-6">
                <h2 className="text-xl font-semibold mb-4">Message Summary</h2>
                <div className="space-y-3">
                  {Object.entries(getSummaryByType()).map(([type, count]) => (
                    <div key={type} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-neutral-700 rounded-lg">
                      <span className="font-medium">{type}</span>
                      <span className="text-blue-600 dark:text-blue-400 font-semibold">{count}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                  <div className="flex justify-between items-center font-semibold">
                    <span>Total Messages</span>
                    <span className="text-blue-600 dark:text-blue-400">{selectedOccasions.length}</span>
                  </div>
                </div>
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    onClick={() => setShowConfirmSend(false)}
                    className="btn-outline"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendMessages}
                    className="btn-primary"
                  >
                    Send Messages
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {
          showResponse && (<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg w-full max-w-md">
              <div className="p-6">
                <h2 className="text-xl font-semibold mb-4">Message Summary</h2>
                <div className="mb-4 w-full flex text-center flex flex-col items-center">
                  <button
                    onClick={handleDetailsButton}
                    className="btn-primary flex items-center gap-2">
                    View Message Delivery Details
                  </button>
                </div>
              </div>
            </div>
          </div>)
        }
      </div>
    </div>
  );
} 